import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useNotifications } from "../../context/NotificationsContext";
import NotificationDetailModal from "../common/NotificationDetailModal";
import { hasNotificationTarget, resolveNotificationTarget } from "../../utils/notificationTarget";
import { isOpenRequest } from "../../utils/requestHelpers";
import { cancelLeave } from "../../api/leave";
import { cancelPermit } from "../../api/permit";
import { cancelAdvance } from "../../api/advances";
import { cancelOvertime } from "../../api/overtime";
import { useNavigation } from "../../context/NavigationContext";
import { getResubmitNavigation } from "../../utils/requestTransparency";
import RequestTransparency from "../common/RequestTransparency";
import "./RequestsNotifications.css";

const statusConfig = {
  pending: { label: "قيد المراجعة", className: "ess-status-review" },
  gm_pending: { label: "بانتظار المدير العام", className: "ess-status-review" },
  manager_approved: { label: "موافقة المدير - بانتظار مدير الإدارة", className: "ess-status-review" },
  approved: { label: "تمت الموافقة", className: "ess-status-approved" },
  rejected: { label: "مرفوض", className: "ess-status-rejected" },
  modified: { label: "تم التعديل", className: "ess-status-review" },
  cancelled: { label: "ملغى", className: "ess-status-rejected" },
};

const typeLabels = {
  "إجازة سنوية": "إجازة اعتيادية",
  "إجازة اعتيادية": "إجازة اعتيادية",
  "إجازة مرضية": "إجازة مرضية",
  "إجازة عارضة": "إجازة عارضة",
  "إجازة على الحساب الخاص": "إجازة على الحساب الخاص",
  "إجازة بدون راتب": "إجازة على الحساب الخاص",
  إذن: "إذن",
  سلفة: "طلب سلفة",
  إضافي: "إضافي",
};

function getStatusInfo(status) {
  return statusConfig[status] || statusConfig.pending;
}

function getRequestIcon(req) {
  if (req.category === "permit" || req.type === "إذن") return "⏱️";
  if (req.category === "leave" || req.type?.includes("إجازة")) return "📅";
  if (req.type === "سلفة") return "💰";
  if (req._source === "overtime" || req.type === "إضافي") return "⏱️";
  return "📄";
}

function getRequestDetail(req) {
  if (req.category === "permit") return `${req.hours} ساعة`;
  if (req.category === "leave") return `${req.days} أيام`;
  if (req.type === "سلفة") return `${req.amount} ج.م`;
  if (req._source === "overtime" || req.type === "إضافي") {
    return `${req.hours} ساعة`;
  }
  return "";
}

function getNotifIcon(type) {
  if (type === "approval") return "✅";
  if (type === "rejection") return "❌";
  if (type === "modified") return "✏️";
  if (type === "forwarded") return "➡️";
  if (type === "new_request") return "📋";
  if (type === "escalated") return "⬆️";
  if (type === "complaint_new" || type === "complaint_response") return "📩";
  if (type === "resignation_new" || type === "resignation_response") return "📋";
  if (type === "appeal" || type === "appeal_response") return "⚖️";
  return "🔔";
}

function RequestCard({ req, onCancel, cancellingId, onResubmit }) {
  const status = getStatusInfo(req.status);
  const detail = getRequestDetail(req);
  const canCancel = isOpenRequest(req);
  const isCancelling = cancellingId === req.id;

  return (
    <div className="ess-notif-card">
      <div className="ess-notif-icon">{getRequestIcon(req)}</div>

      <div className="ess-notif-body">
        <div className="ess-notif-top-row">
          <span className="ess-notif-type">
            {typeLabels[req.type] || req.type}
          </span>
          <span className={`ess-status-badge ${status.className}`}>
            {status.label}
          </span>
        </div>

        <div className="ess-notif-meta">
          {detail && <span>{detail}</span>}
          <span>تاريخ التقديم: {req.submittedAt}</span>
        </div>

        <RequestTransparency request={req} compact onResubmit={onResubmit} />

        {req.status === "modified" && req.managerNotes && (
          <div className="ess-notif-reason">
            ملاحظات المدير: {req.managerNotes}
          </div>
        )}

        {canCancel && (
          <button
            type="button"
            className="ess-notif-cancel-btn"
            disabled={isCancelling}
            onClick={() => onCancel(req)}
          >
            {isCancelling ? "جاري الإلغاء..." : "إلغاء الطلب"}
          </button>
        )}
      </div>
    </div>
  );
}

function RequestsSection({
  title,
  icon,
  requests,
  emptyText,
  onCancel,
  cancellingId,
  onResubmit,
}) {
  if (requests.length === 0) return null;
  return (
    <div className="ess-notif-section">
      <div className="ess-notif-section-header">
        <span className="ess-notif-section-icon">{icon}</span>
        <h2 className="ess-notif-section-title">{title}</h2>
        <span className="ess-notif-section-count">{requests.length}</span>
      </div>
      <div className="ess-notif-list">
        {requests.map((req) => (
          <RequestCard
            key={req.id}
            req={req}
            onCancel={onCancel}
            cancellingId={cancellingId}
            onResubmit={onResubmit}
          />
        ))}
      </div>
    </div>
  );
}

function NotificationCard({ notif, unread, onOpen }) {
  const clickable = hasNotificationTarget(notif);
  return (
    <div
      className={`ess-notif-card ${unread ? "unread" : "read"}${clickable ? " clickable" : ""}`}
      onClick={() => onOpen(notif)}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(notif);
        }
      }}
      style={{ cursor: clickable ? "pointer" : "default" }}
    >
      <div className="ess-notif-icon">{getNotifIcon(notif.type)}</div>
      <div className="ess-notif-body">
        <div className="ess-notif-top-row">
          <span className="ess-notif-type">{notif.title}</span>
          <span
            className={`ess-status-badge ${unread ? "ess-status-review" : "ess-status-approved"}`}
          >
            {unread ? "جديد" : "مقروء"}
          </span>
        </div>
        <div className="ess-notif-meta">
          <span>{notif.message}</span>
        </div>
        {notif.senderName && (
          <div className="ess-notif-meta">
            <span>من: {notif.senderName}</span>
          </div>
        )}
        {clickable && (
          <div className="ess-notif-pending-note">اضغط لعرض كل التفاصيل</div>
        )}
      </div>
    </div>
  );
}

function RequestsNotifications() {
  const { user } = useAuth();
  const { pendingRequests, addPendingRequest } = useData();
  const { notifications, markAsRead, markAllAsRead } = useNotifications();
  const navigation = useNavigation();
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelError, setCancelError] = useState("");

  const myRequests = useMemo(() => {
    return pendingRequests
      .filter(
        (req) =>
          String(req.userId) === String(user?.id) ||
          String(req.employeeCode) === String(user?.code) ||
          String(req.employeeId) === String(user?.employeeId),
      )
      .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  }, [pendingRequests, user]);

  const pending = myRequests.filter(
    (r) => r.status === "pending" || r.status === "gm_pending",
  );
  const managerApproved = myRequests.filter((r) => r.status === "manager_approved");
  const approved = myRequests.filter((r) => r.status === "approved");
  const rejected = myRequests.filter((r) => r.status === "rejected");
  const modified = myRequests.filter((r) => r.status === "modified");

  // Backend notifications are already scoped to the logged-in user (userId).
  // Legacy frontend-only notifications use recipientId.
  const myNotifications = notifications.filter((n) => {
    if (!n.recipientId) return true;
    return (
      String(n.recipientId) === String(user?.id) ||
      String(n.recipientId) === String(user?.code) ||
      String(n.userId) === String(user?.id)
    );
  });

  const unreadNotifications = myNotifications.filter(
    (n) => !(n.isRead ?? n.read),
  );
  const readNotifications = myNotifications.filter(
    (n) => n.isRead ?? n.read,
  );

  function handleOpenNotification(notif) {
    if (!(notif.isRead ?? notif.read)) {
      markAsRead(notif.id);
    }
    const target = resolveNotificationTarget(notif);
    if (target?.kind === "investigation" && target.id) {
      sessionStorage.setItem("hrms-investigation-focus", String(target.id));
      window.dispatchEvent(new Event("investigation:focus"));
      navigation.navigate("investigations");
      return;
    }
    if (target?.kind === "announcement" && target.id) {
      navigation.navigate("announcements", { announcementId: target.id });
      return;
    }
    if (target?.kind === "company_policy" && target.id) {
      navigation.navigate("company-policies", { policyId: target.id });
      return;
    }
    if (hasNotificationTarget(notif)) {
      setSelectedNotification(notif);
    }
  }

  function handleResubmit(request) {
    const target = getResubmitNavigation(request);
    if (!target || !navigation?.navigate) return;
    navigation.navigate(target.id, target.options);
  }

  async function handleCancelRequest(req) {
    if (!req?.id) return;
    if (
      !window.confirm(
        "هل تريد إلغاء هذا الطلب؟ يمكنك تقديم طلب جديد بعد الإلغاء.",
      )
    ) {
      return;
    }

    setCancellingId(req.id);
    setCancelError("");
    try {
      const isPermit =
        req._source === "permit" ||
        req.category === "permit" ||
        req.type === "إذن" ||
        req.type === "إذن تعويضي";
      const isAdvance = req._source === "advance" || req.type === "سلفة";
      const isOvertime = req._source === "overtime" || req.type === "إضافي";

      if (isPermit) {
        await cancelPermit(req.id);
      } else if (isAdvance) {
        await cancelAdvance(req.id);
      } else if (isOvertime) {
        await cancelOvertime(req.id);
      } else {
        await cancelLeave(req.id);
      }
      await addPendingRequest();
    } catch (err) {
      const message = err.response?.data?.message;
      setCancelError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إلغاء الطلب",
      );
    } finally {
      setCancellingId(null);
    }
  }

  const isHrOrApprover =
    user?.role === "hr" ||
    user?.role === "gm" ||
    user?.backendRoles?.includes("hr") ||
    user?.backendRoles?.includes("gm");

  return (
    <>
      <div className="ess-home-header">
        <h1 className="ess-home-title">إشعاراتي</h1>
        <p className="ess-home-subtitle">
          {isHrOrApprover
            ? "اضغط على أي إشعار لعرض كل تفاصيل الطلب المرتبط به"
            : "متابعة كل الطلبات اللي قدمتها وحالة كل طلب"}
        </p>
      </div>

      {/* قسم الإشعارات الجديدة (غير المقروءة) */}
      {unreadNotifications.length > 0 && (
        <div className="ess-notif-section">
          <div className="ess-notif-section-header">
            <span className="ess-notif-section-icon">🔔</span>
            <h2 className="ess-notif-section-title">إشعارات جديدة</h2>
            <span className="ess-notif-section-count">
              {unreadNotifications.length}
            </span>
            <button
              type="button"
              className="ess-notif-mark-all-btn"
              onClick={markAllAsRead}
            >
              تحديد الكل كمقروء
            </button>
          </div>
          <div className="ess-notif-list">
            {unreadNotifications.map((notif) => (
              <NotificationCard
                key={notif.id}
                notif={notif}
                unread
                onOpen={handleOpenNotification}
              />
            ))}
          </div>
        </div>
      )}

      {/* قسم الإشعارات المقروءة السابقة */}
      {readNotifications.length > 0 && (
        <div className="ess-notif-section">
          <div className="ess-notif-section-header">
            <span className="ess-notif-section-icon">📨</span>
            <h2 className="ess-notif-section-title">إشعارات سابقة</h2>
            <span className="ess-notif-section-count">
              {readNotifications.length}
            </span>
          </div>
          <div className="ess-notif-list">
            {readNotifications.map((notif) => (
              <NotificationCard
                key={notif.id}
                notif={notif}
                unread={false}
                onOpen={handleOpenNotification}
              />
            ))}
          </div>
        </div>
      )}

      <div className="ess-notif-summary">
        <span className="ess-notif-summary-count">{myRequests.length}</span>
        <span className="ess-notif-summary-text">
          إجمالي الطلبات{" "}
          {pending.length > 0 && (
            <strong className="ess-notif-summary-pending">
              — {pending.length} قيد المراجعة
            </strong>
          )}
        </span>
      </div>

      {myRequests.length === 0 && (
        <p className="ess-empty-message">لا توجد طلبات مقدمة حتى الآن</p>
      )}

      {cancelError && <p className="ess-notif-cancel-error">{cancelError}</p>}

      <RequestsSection
        title="قيد المراجعة"
        icon="⏳"
        requests={pending}
        onCancel={handleCancelRequest}
        cancellingId={cancellingId}
        onResubmit={handleResubmit}
      />

      {managerApproved.length > 0 && (
        <RequestsSection
          title="موافقة المدير - بانتظار مدير الإدارة"
          icon="⏳"
          requests={managerApproved}
          onCancel={handleCancelRequest}
          cancellingId={cancellingId}
          onResubmit={handleResubmit}
        />
      )}

      {modified.length > 0 && (
        <RequestsSection
          title="طلبات تم تعديلها"
          icon="✏️"
          requests={modified}
          onCancel={handleCancelRequest}
          cancellingId={cancellingId}
          onResubmit={handleResubmit}
        />
      )}

      <RequestsSection
        title="طلبات تمت الموافقة عليها"
        icon="✅"
        requests={approved}
        onCancel={handleCancelRequest}
        cancellingId={cancellingId}
        onResubmit={handleResubmit}
      />

      <RequestsSection
        title="طلبات مرفوضة"
        icon="❌"
        requests={rejected}
        onCancel={handleCancelRequest}
        cancellingId={cancellingId}
        onResubmit={handleResubmit}
      />

      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
        />
      )}
    </>
  );
}

export default RequestsNotifications;
